import { Fragment, type ReactNode } from 'react'

/**
 * A very small Markdown renderer for the About and Contact page bodies.
 *
 * A full Markdown library would add tens of kilobytes to every page load, and
 * would then need sanitising on top, because the text comes from a database.
 * This handles what the pages actually use — paragraphs, headings, lists,
 * links, bold and italic — and builds React elements rather than HTML strings,
 * so there is no dangerouslySetInnerHTML anywhere and injection is impossible
 * by construction.
 */
export function Markdown({ source, className = '' }: { source: string; className?: string }) {
  if (!source.trim()) return null

  const blocks = source.replace(/\r\n/g, '\n').split(/\n{2,}/)

  return (
    <div className={`space-y-4 leading-relaxed text-stone-700 ${className}`}>
      {blocks.map((block, index) => (
        <Block key={index} source={block.trim()} />
      ))}
    </div>
  )
}

function Block({ source }: { source: string }) {
  if (!source) return null

  const heading = /^(#{1,3})\s+(.*)$/.exec(source)
  if (heading) {
    const level = heading[1]?.length ?? 1
    const text = heading[2] ?? ''
    const classes = ['text-3xl mt-10', 'text-2xl mt-8', 'text-xl mt-6']
    if (level === 1) return <h2 className={classes[0]}>{inline(text)}</h2>
    if (level === 2) return <h3 className={classes[1]}>{inline(text)}</h3>
    return <h4 className={classes[2]}>{inline(text)}</h4>
  }

  const lines = source.split('\n')
  if (lines.every((line) => /^\s*[-*]\s+/.test(line))) {
    return (
      <ul className="list-disc space-y-1 pl-5">
        {lines.map((line, index) => (
          <li key={index}>{inline(line.replace(/^\s*[-*]\s+/, ''))}</li>
        ))}
      </ul>
    )
  }

  if (lines.every((line) => /^\s*\d+[.)]\s+/.test(line))) {
    return (
      <ol className="list-decimal space-y-1 pl-5">
        {lines.map((line, index) => (
          <li key={index}>{inline(line.replace(/^\s*\d+[.)]\s+/, ''))}</li>
        ))}
      </ol>
    )
  }

  return (
    <p>
      {lines.map((line, index) => (
        <Fragment key={index}>
          {index > 0 && <br />}
          {inline(line)}
        </Fragment>
      ))}
    </p>
  )
}

// Matches, in order: [text](url), **bold**, *italic*, `code`.
const INLINE_PATTERN = /(\[([^\]]+)\]\(([^)\s]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)/g

function inline(text: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0

  INLINE_PATTERN.lastIndex = 0
  while ((match = INLINE_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }

    if (match[2] !== undefined && match[3] !== undefined) {
      const href = match[3]
      // Only http(s), mailto and tel links are allowed through — this is what
      // stops a javascript: URL from ever reaching the DOM.
      const safe = /^(https?:|mailto:|tel:|\/)/i.test(href)
      nodes.push(
        safe ? (
          <a
            key={key++}
            href={href}
            className="link-underline"
            {...(href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {match[2]}
          </a>
        ) : (
          match[2]
        )
      )
    } else if (match[5] !== undefined) {
      nodes.push(
        <strong key={key++} className="font-semibold">
          {match[5]}
        </strong>
      )
    } else if (match[7] !== undefined) {
      nodes.push(<em key={key++}>{match[7]}</em>)
    } else if (match[9] !== undefined) {
      nodes.push(
        <code key={key++} className="rounded bg-stone-100 px-1 py-0.5 text-sm">
          {match[9]}
        </code>
      )
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }
  return nodes
}
